import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { strings } from "../../utils/constants";
import { getCMSByKey } from "../../redux/Actions";
import { getPublicVideos, getPublicVideoPlayUrl } from "../../services/videoApi";
import { CircularProgress } from "@mui/material";

/**
 * Normalize the list response into an array of video items. The envelope is
 * { data: { items: [...] } }, but we accept a few shapes defensively.
 */
const extractVideoItems = (res) => {
  if (!res) return [];
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  return [];
};

/* ─── helpers ───────────────────────────────────────────── */
const formatTime = (secs) => {
  if (isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/* ─── Custom video player ───────────────────────────────── */
const VideoPlayer = ({ src }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const seekRef = useRef(null);
  const containerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef(null);

  /* reset state when src changes — handled by key={activeIndex} remount,
     but also handle the case where src prop changes without remount */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setPlaying(false);
    setBuffering(false);
    setCurrentTime(0);
    setDuration(0);
    setShowControls(true);
    video.pause();
    video.load();
  }, [src]);

  /* auto-hide controls after 3s of inactivity while playing */
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    if (playing) {
      hideTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [playing]);

  useEffect(() => {
    resetHideTimer();
    return () => clearTimeout(hideTimer.current);
  }, [playing, resetHideTimer]);

  /* fullscreen change listener */
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  /* ── handlers ── */
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => { }); } else { v.pause(); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const handleVolumeChange = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const val = parseFloat(e.target.value);
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setMuted(val === 0);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = seekRef.current.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    v.currentTime = ratio * duration;
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) {
      el.requestFullscreen && el.requestFullscreen();
    } else {
      document.exitFullscreen && document.exitFullscreen();
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={`vp-wrap${isFullscreen ? " vp-fullscreen" : ""}${!showControls ? " vp-hide-cursor" : ""}`}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      {/* video element — no native controls */}
      <video
        ref={videoRef}
        className="vp-video"
        playsInline
        preload="metadata"
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => { setPlaying(false); setShowControls(true); }}
        onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
        onEnded={() => { setPlaying(false); setShowControls(true); }}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => setBuffering(false)}
        onPlaying={() => setBuffering(false)}
      >
        <source src={src} type="video/mp4" />
      </video>

      {/* big play button when paused */}
      {!playing && !buffering && (
        <button
          type="button"
          className="vp-center-play"
          onClick={togglePlay}
          aria-label={t(strings.translate.VIDEO_PLAY)}
          title={t(strings.translate.VIDEO_PLAY)}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>
      )}

      {/* buffering spinner */}
      {buffering && (
        <div className="vp-buffering" aria-label="Buffering">
          <svg className="vp-spinner" viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeDasharray="80 40" />
          </svg>
        </div>
      )}

      {/* controls bar */}
      <div className={`vp-controls${showControls ? " vp-controls--visible" : ""}`}>
        {/* seek bar */}
        <div
          ref={seekRef}
          className="vp-seek"
          role="slider"
          aria-label={t(strings.translate.VIDEO_SEEK)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          tabIndex={0}
          onClick={handleSeek}
          onKeyDown={(e) => {
            const v = videoRef.current;
            if (!v) return;
            if (e.key === "ArrowRight") v.currentTime = Math.min(v.currentTime + 5, duration);
            if (e.key === "ArrowLeft") v.currentTime = Math.max(v.currentTime - 5, 0);
          }}
          title={t(strings.translate.VIDEO_SEEK)}
        >
          <div className="vp-seek-track">
            <div className="vp-seek-fill" style={{ width: `${progress}%` }} />
            <div className="vp-seek-thumb" style={{ left: `${progress}%` }} />
          </div>
        </div>

        {/* bottom row */}
        <div className="vp-controls-row">
          <div className="vp-controls-left">
            {/* play/pause */}
            <button
              type="button"
              className="vp-btn"
              onClick={togglePlay}
              aria-label={playing ? t(strings.translate.VIDEO_PAUSE) : t(strings.translate.VIDEO_PLAY)}
              title={playing ? t(strings.translate.VIDEO_PAUSE) : t(strings.translate.VIDEO_PLAY)}
            >
              {playing ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              )}
            </button>

            {/* mute/unmute */}
            <button
              type="button"
              className="vp-btn"
              onClick={toggleMute}
              aria-label={muted ? t(strings.translate.VIDEO_UNMUTE) : t(strings.translate.VIDEO_MUTE)}
              title={muted ? t(strings.translate.VIDEO_UNMUTE) : t(strings.translate.VIDEO_MUTE)}
            >
              {muted || volume === 0 ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06A8.99 8.99 0 0 0 17.73 19L19 20.27 20.27 19 5.27 4 4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg>
              ) : volume < 0.5 ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
              )}
            </button>

            {/* volume slider */}
            <div className="vp-volume-wrap" title={t(strings.translate.VIDEO_VOLUME)}>
              <input
                type="range"
                className="vp-volume"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label={t(strings.translate.VIDEO_VOLUME)}
              />
            </div>

            {/* time */}
            <span className="vp-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="vp-controls-right">
            {/* fullscreen */}
            <button
              type="button"
              className="vp-btn"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? t(strings.translate.VIDEO_EXIT_FULLSCREEN) : t(strings.translate.VIDEO_FULLSCREEN)}
              title={isFullscreen ? t(strings.translate.VIDEO_EXIT_FULLSCREEN) : t(strings.translate.VIDEO_FULLSCREEN)}
            >
              {isFullscreen ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main component ────────────────────────────────────── */
const DesktopAppPromotion = ({ onLoadApp }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { language } = useSelector((state) => state.generalReducer);
  // Sidebar heading ("ADA FEATURES") pulled from the "ada-subtitle" CMS page as
  // rich HTML. Falls back to the static translation when unavailable.
  const [adaSubtitleHtml, setAdaSubtitleHtml] = useState("");
  // `features` holds the real video list fetched from the public video API.
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  // Playback URL for the currently active video (short-lived signed S3 URL),
  // fetched on demand from GET /{lang}/admin/videos/:id/play-url.
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [videoUrlLoading, setVideoUrlLoading] = useState(false);

  // The layout must fill exactly the viewport space BELOW whatever sits above it
  // (site header + page banner), so the video shrinks to fit and the CTA buttons
  // (View Previous / View Next / Load Desktop) are always visible without
  // scrolling. The header/banner height is dynamic, so we measure the layout's
  // top offset and set its height to the remaining viewport height.
  const layoutRef = useRef(null);
  const [layoutHeight, setLayoutHeight] = useState(null);

  useEffect(() => {
    const recomputeHeight = () => {
      const el = layoutRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Leave a small bottom gutter so the CTA isn't flush against the edge.
      const available = window.innerHeight - top;
      setLayoutHeight(available > 320 ? available : 320);
    };

    recomputeHeight();
    window.addEventListener("resize", recomputeHeight);
    // Recompute after fonts/images settle.
    const tid = setTimeout(recomputeHeight, 150);
    return () => {
      window.removeEventListener("resize", recomputeHeight);
      clearTimeout(tid);
    };
  }, [loading, features]);

  // Fetch the list of active videos for the promotional page.
  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPublicVideos(language, {
        page: 1,
        limit: 50,
        status: "active",
      });
      const items = extractVideoItems(response);
      setFeatures(items);
    } catch (error) {
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Reset selection whenever the list changes (e.g. language switch).
  useEffect(() => {
    setActiveIndex(0);
  }, [features]);

  // Fetch the sidebar heading from the "ada-subtitle" CMS page. Re-fetches on
  // language change so the correct (content / content_fr) variant is shown.
  useEffect(() => {
    let cancelled = false;
    const fetchAdaSubtitle = async () => {
      try {
        const response = await dispatch(
          getCMSByKey({ language, key: strings.cmsPages.ADA_SUBTITLE })
        );
        const data = response?.payload?.data;
        if (!cancelled && response?.payload?.success && data) {
          const html =
            language === strings.language.FR
              ? data.content_fr || data.content
              : data.content;
          setAdaSubtitleHtml(html || "");
        }
      } catch (_) {
        // keep the translation fallback on error
      }
    };
    fetchAdaSubtitle();
    return () => {
      cancelled = true;
    };
  }, [dispatch, language]);

  // Title to show for a video, following the current UI language: French title
  // on the FR site (falling back to the English title when none exists).
  const displayTitle = (feature) => {
    if (!feature) return "";
    if (language === strings.language.FR) {
      return feature.titleFr || feature.title_fr || feature.title || "";
    }
    return feature.title || "";
  };

  const activeFeature = features[activeIndex] || {};
  const activeVideoId = activeFeature.id;
  // Whether the active video has a French file.
  const activeHasFr = !!(
    activeFeature.hasFrVideo ||
    activeFeature.fileNameFr ||
    activeFeature.file_name_fr
  );
  // On the French site, play the French video when it exists; otherwise fall
  // back to English. On the English site, always play English.
  const desiredVideoLang =
    language === strings.language.FR && activeHasFr
      ? strings.language.FR
      : strings.language.EN;

  // Load a fresh playback URL whenever the active video (or desired language)
  // changes.
  useEffect(() => {
    let cancelled = false;

    if (!activeVideoId) {
      setActiveVideoUrl("");
      return undefined;
    }

    setVideoUrlLoading(true);
    getPublicVideoPlayUrl(language, activeVideoId, desiredVideoLang)
      .then((res) => {
        if (cancelled) return;
        if (res?.playUrl) {
          setActiveVideoUrl(res.playUrl);
        } else if (desiredVideoLang === strings.language.FR) {
          // French file unavailable at play time — fall back to English.
          return getPublicVideoPlayUrl(
            language,
            activeVideoId,
            strings.language.EN
          ).then((enRes) => {
            if (!cancelled) setActiveVideoUrl(enRes?.playUrl || "");
          });
        } else {
          setActiveVideoUrl("");
        }
      })
      .catch(() => {
        if (!cancelled) setActiveVideoUrl("");
      })
      .finally(() => {
        if (!cancelled) setVideoUrlLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeVideoId, language, desiredVideoLang]);

  const switchVideo = (index) => {
    if (index < 0 || index >= features.length) return;
    setActiveIndex(index);
    setSidebarOpen(false);
  };

  const goToNext = () => {
    if (activeIndex + 1 < features.length) switchVideo(activeIndex + 1);
  };

  const goToPrevious = () => {
    if (activeIndex - 1 >= 0) switchVideo(activeIndex - 1);
  };

  const isFirstItem = activeIndex <= 0;
  const isLastItem = activeIndex >= features.length - 1;

  return (
    <>
      <style>{adaStyles}</style>
      <div
        ref={layoutRef}
        className={`ada-layout${desktopCollapsed ? " desktop-collapsed" : ""}`}
        style={layoutHeight ? { height: `${layoutHeight}px` } : undefined}
      >

        {loading ? (
          /* Centered loader while CMS data loads */
          <div className="ada-loading-centered">
            <CircularProgress color="success" size={50} />
          </div>
        ) : (
          <>
            {/* ── Sidebar ── */}
            {sidebarOpen && (
              <div className="ada-backdrop" onClick={() => setSidebarOpen(false)} />
            )}

            <aside className={`ada-sidebar${sidebarOpen ? " open" : ""}`}>
              <div className="ada-sidebar-header">
                {adaSubtitleHtml ? (
                  <h3
                    dangerouslySetInnerHTML={{ __html: adaSubtitleHtml }}
                  />
                ) : (
                  <h3>{t(strings.translate.ADA_FEATURES)}</h3>
                )}
                <button
                  type="button"
                  className="ada-icon-btn ada-btn-collapse-desktop"
                  onClick={() => setDesktopCollapsed(true)}
                  aria-label={t(strings.translate.HIDE_FEATURES)}
                  title={t(strings.translate.HIDE_FEATURES)}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M9 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M15 9l-3 3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className="ada-sidebar-list">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`ada-sidebar-item${index === activeIndex ? " active" : ""}`}
                    onClick={() => switchVideo(index)}
                  >
                    <p className="ada-item-title">{displayTitle(feature)}</p>
                  </div>
                ))}
              </div>
            </aside>

            {/* ── Main content ── */}
            <main className="ada-main">

              {/* Desktop topbar — shown only when sidebar is collapsed */}
              <div className="ada-desktop-topbar">
                <button
                  type="button"
                  className="ada-icon-btn"
                  onClick={() => setDesktopCollapsed(false)}
                  aria-label={t(strings.translate.SHOW_FEATURES)}
                  title={t(strings.translate.SHOW_FEATURES)}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M9 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M13 9l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Mobile topbar */}
              <div className="ada-topbar">
                <button
                  type="button"
                  className="ada-icon-btn ada-menu-btn"
                  onClick={() => setSidebarOpen(true)}
                  aria-label={t(strings.translate.SHOW_FEATURES)}
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <span className="ada-topbar-title">
                  {displayTitle(activeFeature) || t(strings.translate.ADA_FEATURES)}
                </span>
              </div>

              {/* Custom video player */}
              <div className="ada-video-wrap">
                {videoUrlLoading ? (
                  <div className="ada-video-placeholder">
                    <CircularProgress color="inherit" size={44} />
                  </div>
                ) : activeVideoUrl ? (
                  <VideoPlayer src={activeVideoUrl} key={activeVideoId} />
                ) : (
                  <div className="ada-video-placeholder">
                    <span>{t(strings.translate.NO_VIDEOS_AVAILABLE)}</span>
                  </div>
                )}
              </div>

              {/* CTA bar */}
              <div className="ada-cta">
                <div className="ada-cta-nav">
                  {!isFirstItem && (
                    <button
                      type="button"
                      className="ada-btn ada-btn--outline"
                      onClick={goToPrevious}
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {t(strings.translate.VIEW_PREVIOUS_FEATURE)}
                    </button>
                  )}
                  {!isLastItem && (
                    <button
                      type="button"
                      className="ada-btn ada-btn--primary"
                      onClick={goToNext}
                    >
                      {t(strings.translate.VIEW_NEXT_FEATURE)}
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>

                {onLoadApp && (
                  <div className="ada-cta-launch">
                    <p>{t(strings.translate.CLICK_HERE_TO_LAUNCH_APP)}</p>
                    <button
                      type="button"
                      className="ada-btn ada-btn--primary"
                      onClick={onLoadApp}
                    >
                      {t(strings.translate.LOAD_DESKTOP_APPLICATION)}
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </main>
          </>
        )}
      </div>
    </>
  );
};

const adaStyles = `
  /* ─── Tokens ───────────────────────────────────────────── */
  .ada-layout {
    --ada-green:        #184437;
    --ada-green-hover:  #2a6b55;
    --ada-green-tint:   #e9fff8;
    --ada-border:       #e5e7eb;
    --ada-bg-sidebar:   #f7f8fa;
    --ada-text-dark:    #474748;
    --ada-text-muted:   #6f6f6f;
    --ada-sidebar-w:    320px;
    --ada-radius:       16px;
  }

  /* ─── Loading (centered inside layout) ─────────────────── */
  .ada-loading-centered {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    z-index: 10;
  }

  /* ─── Page layout ───────────────────────────────────────── */
  /* Height is set dynamically (inline) to the exact remaining viewport space
     below the header/banner so the CTA is always visible. The calc() here is a
     fallback for the brief moment before the measured height is applied. */
  .ada-layout {
    display: flex;
    align-items: stretch;
    background: #fff;
    position: relative;
    height: calc(100vh - 220px);
    min-height: 320px;
    overflow: hidden;
  }

  /* ─── Sidebar ───────────────────────────────────────────── */
  .ada-sidebar {
    width: var(--ada-sidebar-w);
    flex-shrink: 0;
    background: #fff;
    border-right: 1px solid var(--ada-border);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    transition: width 0.25s ease, border 0.25s ease;
  }

  .ada-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--ada-bg-sidebar);
    border-bottom: 1px solid var(--ada-border);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .ada-sidebar-header h3 {
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--ada-text-dark);
    margin: 0;
  }

  .ada-btn-collapse-desktop { display: flex; }

  /* ─── Desktop collapsed state ───────────────────────────── */
  .ada-layout.desktop-collapsed .ada-sidebar {
    width: 0;
    border-right: none;
    overflow: hidden;
  }

  .ada-desktop-topbar {
    display: none;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid var(--ada-border);
    flex-shrink: 0;
    background: #fff;
  }

  .ada-layout.desktop-collapsed .ada-desktop-topbar { display: flex; }

  .ada-sidebar-list { flex: 1; }

  .ada-sidebar-item {
    padding: 14px 16px;
    border-bottom: 1px solid var(--ada-border);
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: background 0.12s;
  }

  .ada-sidebar-item:last-child { border-bottom: none; }
  .ada-sidebar-item:hover { background: var(--ada-bg-sidebar); }

  .ada-sidebar-item.active {
    background: var(--ada-green-tint);
    border-left-color: var(--ada-green);
  }

  .ada-item-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--ada-text-dark);
    margin: 0;
    line-height: 1.4;
  }

  .ada-sidebar-item.active .ada-item-title {
    color: var(--ada-green);
    font-weight: 700;
  }

  /* ─── Backdrop ──────────────────────────────────────────── */
  .ada-backdrop { display: none; }

  /* ─── Main area ─────────────────────────────────────────── */
  .ada-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .ada-topbar { display: none; }

  /* ─── Icon buttons ───────────────────────────────────────── */
  .ada-icon-btn {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .ada-icon-btn:hover { background: rgba(0,0,0,0.06); }

  .ada-icon-btn svg {
    width: 20px;
    height: 20px;
    color: var(--ada-text-dark);
  }

  /* ─── Video wrap ────────────────────────────────────────── */
  .ada-video-wrap {
    flex: 1;
    min-height: 0;
    margin: 16px;
    border-radius: var(--ada-radius);
    overflow: hidden;
    background: #000;
  }

  .ada-video-placeholder {
    width: 100%;
    height: 100%;
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    text-align: center;
    padding: 24px;
  }

  /* ════════════════════════════════════════════════════════
     CUSTOM VIDEO PLAYER
     ════════════════════════════════════════════════════════ */
  .vp-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background: #000;
    border-radius: var(--ada-radius, 16px);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vp-hide-cursor,
  .vp-hide-cursor * { cursor: none; }

  .vp-video {
    display: block;
    width: 100%;
    height: 100%;
    /* Fill the frame edge-to-edge (removes the black letterbox/pillarbox bars).
       May crop a little of the video edges when aspect ratios differ. */
    object-fit: cover;
    cursor: pointer;
  }

  /* big centre play button */
  .vp-center-play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: none;
    background: rgba(0,0,0,0.55);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.18s, transform 0.18s;
    z-index: 2;
  }

  .vp-center-play:hover {
    background: rgba(0,0,0,0.75);
    transform: translate(-50%, -50%) scale(1.1);
  }

  .vp-center-play svg { width: 32px; height: 32px; }

  /* buffering spinner */
  .vp-buffering {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    pointer-events: none;
  }

  .vp-spinner {
    width: 48px;
    height: 48px;
    animation: vp-spin 0.9s linear infinite;
    filter: drop-shadow(0 0 4px rgba(0,0,0,0.5));
  }

  @keyframes vp-spin { to { transform: rotate(360deg); } }

  /* controls bar */
  .vp-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 12px 10px;
    background: linear-gradient(transparent, rgba(0,0,0,0.75));
    opacity: 0;
    transition: opacity 0.22s;
    z-index: 3;
    border-radius: 0 0 var(--ada-radius, 16px) var(--ada-radius, 16px);
  }

  .vp-controls--visible { opacity: 1; }

  .vp-seek {
    width: 100%;
    padding: 8px 0;
    cursor: pointer;
    outline: none;
  }

  .vp-seek-track {
    position: relative;
    height: 4px;
    background: rgba(255,255,255,0.3);
    border-radius: 2px;
  }

  .vp-seek-fill {
    position: absolute;
    top: 0; left: 0;
    height: 100%;
    background: #fff;
    border-radius: 2px;
    pointer-events: none;
  }

  .vp-seek-thumb {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    pointer-events: none;
    transition: transform 0.1s;
  }

  .vp-seek:hover .vp-seek-thumb { transform: translate(-50%, -50%) scale(1.3); }

  .vp-controls-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  }

  .vp-controls-left,
  .vp-controls-right {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .vp-btn {
    width: 34px;
    height: 34px;
    border: none;
    background: transparent;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s;
  }

  .vp-btn:hover { background: rgba(255,255,255,0.15); }
  .vp-btn svg { width: 20px; height: 20px; }

  .vp-volume-wrap {
    display: flex;
    align-items: center;
    width: 72px;
  }

  .vp-volume {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255,255,255,0.35);
    outline: none;
    cursor: pointer;
  }

  .vp-volume::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
  }

  .vp-volume::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    border: none;
  }

  .vp-time {
    font-size: 12px;
    color: #fff;
    white-space: nowrap;
    padding-left: 4px;
    font-variant-numeric: tabular-nums;
  }

  .vp-fullscreen { border-radius: 0 !important; }
  /* In fullscreen show the complete video (no cropping) — bars here are fine. */
  .vp-fullscreen .vp-video { height: 100vh; object-fit: contain; }

  /* ─── CTA bar ────────────────────────────────────────────── */
  .ada-cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid var(--ada-border);
    flex-shrink: 0;
    background: #fff;
  }

  .ada-cta-nav {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ada-cta-launch {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    margin-left: auto;
  }

  .ada-cta-launch p {
    font-size: 12px;
    color: var(--ada-text-muted);
    margin: 0;
  }

  /* ─── Buttons ────────────────────────────────────────────── */
  .ada-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 6px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    border: none;
    transition: background 0.18s, color 0.18s;
  }

  .ada-btn svg { width: 14px; height: 14px; flex-shrink: 0; }
  .ada-btn--primary { background: var(--ada-green); color: #fff; }
  .ada-btn--primary:hover { background: var(--ada-green-hover); }
  .ada-btn--outline { background: #fff; color: var(--ada-green); border: 1.5px solid var(--ada-green); }
  .ada-btn--outline:hover { background: var(--ada-green-tint); }

  /* ════════════════════════════════════════════════════════════
     RESPONSIVE
     ════════════════════════════════════════════════════════════ */
  @media (max-width: 991px) {
    .ada-layout { height: auto; min-height: 100px; }

    .ada-sidebar {
      position: absolute;
      top: 0; left: 0;
      height: 100%;
      width: 280px;
      z-index: 400;
      transform: translateX(-100%);
      transition: transform 0.28s ease;
      box-shadow: 4px 0 20px rgba(0,0,0,0.12);
    }

    .ada-sidebar.open { transform: translateX(0); }
    .ada-layout.desktop-collapsed .ada-desktop-topbar { display: none; }

    .ada-backdrop {
      display: block;
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.35);
      z-index: 399;
    }

    .ada-topbar {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 14px;
      border-bottom: 1px solid var(--ada-border);
      flex-shrink: 0;
      background: #fff;
    }

    .ada-topbar-title {
      font-size: 14px;
      font-weight: 700;
      color: var(--ada-text-dark);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ada-main { width: 100%; }

    .ada-video-wrap {
      margin: 12px;
      border-radius: 12px;
      flex: none;
      aspect-ratio: 16 / 9;
    }

    .vp-wrap { border-radius: 12px; }
    .ada-cta { padding: 14px 16px; }
    .ada-cta-launch { align-items: flex-start; margin-left: 0; }
  }

  @media (max-width: 599px) {
    .ada-video-wrap {
      margin: 10px;
      border-radius: 10px;
      aspect-ratio: 16 / 9;
    }

    .vp-wrap { border-radius: 10px; }
    .vp-volume-wrap { width: 52px; }

    .ada-cta {
      flex-direction: column;
      align-items: stretch;
      padding: 12px;
      gap: 10px;
    }

    .ada-cta-nav { width: 100%; flex-direction: column; }
    .ada-cta-launch { width: 100%; align-items: stretch; }
    .ada-cta-launch p { text-align: center; }
    .ada-btn { width: 100%; justify-content: center; padding: 12px 16px; }
  }
`;

export default DesktopAppPromotion;
